/** Mount worker-filtered coastal habitat using the ordinary flora materials,
 * instancing, wind, LOD and tile teardown. No main-thread terrain queries. */
import * as THREE from 'three';
import type { FloraLibrary } from './library';
import type { ClutterSets } from './clutter';
import type { WetlandData, WetlandKind } from './wetlandData';
import { floraHeight, floraTime } from './material';
import { makePooledFloraMesh } from './materialPool';
import { prepareScatterBounds } from './scatterLod';

export function mountWetland(data:WetlandData,library:FloraLibrary,clutter:ClutterSets) {
  const meshes:THREE.InstancedMesh[]=[],colliders:{x:number;y:number;z:number;r:number;h:number}[]=[];
  const groups=new Map<string,{geo:THREE.BufferGeometry;coarse?:THREE.BufferGeometry;kind:WetlandKind;items:{matrix:THREE.Matrix4;evidence:object}[]}>();
  const Y=new THREE.Vector3(0,1,0),up=new THREE.Vector3(),pos=new THREE.Vector3(),q=new THREE.Quaternion(),spin=new THREE.Quaternion(),scale=new THREE.Vector3();
  for(const p of data) {
    const key=`${p.kind}:${p.variant}`;let group=groups.get(key);
    if(!group) {
      const geo=p.kind==='mangrove'?library.variants('mangrove','summer',2)[p.variant]:clutter[p.kind][p.variant];
      const coarse=p.kind==='mangrove'?library.variantsFar('mangrove','summer',2)[p.variant]:p.kind==='shrub'?clutter.shrubCoarse[p.variant]:undefined;
      groups.set(key,group={geo,coarse,kind:p.kind,items:[]});
    }
    up.fromArray(p.normal);q.setFromUnitVectors(Y,up).multiply(spin.setFromAxisAngle(Y,p.yaw));
    pos.fromArray(p.dir).multiplyScalar(p.radius);scale.set(p.size,p.size*(p.kind==='grass'?.65:1),p.size);
    group.items.push({matrix:new THREE.Matrix4().compose(pos,q,scale),evidence:{...p,position:pos.toArray()}});
    if(p.kind==='mangrove')colliders.push({x:pos.x,y:pos.y,z:pos.z,r:.48*1.19*p.size+.06,h:12*p.size});
  }
  for(const g of groups.values()) {
    const fadeFar=g.kind==='mangrove'?950:g.kind==='shrub'?330:240;
    const mesh=makePooledFloraMesh(g.geo,g.items.length,{birth:floraTime(),fadeFar,height:floraHeight(g.geo)});
    g.items.forEach((p,i)=>mesh.setMatrixAt(i,p.matrix));mesh.instanceMatrix.needsUpdate=true;
    Object.assign(mesh.userData,{wetland:g.kind,wetlandCover:g.kind!=='mangrove',fadeFar,coverEvidence:g.items.map(p=>p.evidence),
      scatterLodNear:g.kind==='mangrove'?200:100,scatterLodFar:g.kind==='mangrove'?260:135});
    mesh.castShadow=g.kind==='mangrove';mesh.receiveShadow=true;mesh.visible=false;
    prepareScatterBounds(mesh,g.coarse);meshes.push(mesh);
  }
  return {meshes,colliders};
}
